import { useMemo, useState } from 'react'
import { useAuroraStore } from '../../store/auroraStore'
import { activeCycle, domainSeries, focusTargets, levelHistoryBySkill, skillDomainKeys, type DomainSeries } from '../../store/selectors'
import { domainColor, domainLabel } from '../../lib/rubric'

/**
 * Progress over time: one line per domain, levels 1–5.
 *
 * Each line is the domain's mean of observed skills, followed backwards
 * through the level history — the same number as the "where she is" bar,
 * so the chart never disagrees with the list. Two windows: this cycle, and
 * everything. Focus skills carry a dashed reach from where they are today to
 * the cycle's target level on its last day — the projection.
 */

const DAY = 86_400_000
const W = 640
const H = 220
const PAD = { l: 26, r: 14, t: 10, b: 24 }

export function OverTimeChart() {
  const series = useAuroraStore(domainSeries) as DomainSeries[]
  const cycle = useAuroraStore(activeCycle) as any
  const targets = useAuroraStore(focusTargets)
  const history = useAuroraStore(levelHistoryBySkill)
  const skillDomain = useAuroraStore(skillDomainKeys)
  const [window_, setWindow] = useState<'cycle' | 'all'>('cycle')
  const [focus, setFocus] = useState<string | null>(null)

  const now = Date.now()
  const cycleStart = cycle?.startDate ?? cycle?.start_date
  const cycleEnd = cycle?.endDate ?? cycle?.end_date
  const earliest = useMemo(
    () => Math.min(now, ...series.flatMap((s) => s.points.map((p) => p.at))),
    [series, now],
  )
  const from = window_ === 'cycle' && cycleStart ? Date.parse(`${cycleStart}T00:00:00Z`) : earliest - 3 * DAY
  const to = cycleEnd ? Date.parse(`${cycleEnd}T23:59:59Z`) : now + 14 * DAY
  const x = (t: number) => PAD.l + ((t - from) / Math.max(1, to - from)) * (W - PAD.l - PAD.r)
  const y = (l: number) => PAD.t + (1 - (Math.max(1, Math.min(5, l)) - 1) / 4) * (H - PAD.t - PAD.b)

  const lines = useMemo(() => series.map((s) => {
    let start: number | undefined
    const seq: Array<{ at: number; level: number }> = []
    for (const p of s.points) {
      if (p.at < from) start = p.level
      else seq.push(p)
    }
    if (start !== undefined) seq.unshift({ at: from, level: start })
    if (!seq.length) return { ...s, d: '', last: undefined as number | undefined }
    const d: string[] = []
    seq.forEach((p, i) => {
      if (i === 0) d.push(`M${x(p.at).toFixed(1)},${y(p.level).toFixed(1)}`)
      else {
        const prev = seq[i - 1]!
        d.push(`H${x(p.at).toFixed(1)}`)
        if (p.level !== prev.level) d.push(`V${y(p.level).toFixed(1)}`)
      }
    })
    d.push(`H${x(Math.min(now, to)).toFixed(1)}`)
    return { ...s, d: d.join(' '), last: seq[seq.length - 1]!.level }
  }), [series, from, to, now])

  // Projections: focus skills, from their current level today to the target on the cycle's last day.
  const projections = useMemo(() => targets.flatMap((t) => {
    if (typeof t.targetLevel !== 'number' || !cycleEnd) return []
    const pts = history[t.skillKey]
    const current = pts?.length ? pts[pts.length - 1]!.level : undefined
    if (current === undefined) return []
    return [{
      key: t.skillKey,
      domain: t.domainKey ?? skillDomain[t.skillKey],
      from: current,
      to: t.targetLevel,
      label: t.skillKey.split('.')[1] ?? t.skillKey,
    }]
  }), [targets, history, cycleEnd, skillDomain])

  if (!series.length) {
    return (
      <div className="chart-empty muted">
        No level history yet. As Sapling moves levels on the evidence, the lines appear here.
      </div>
    )
  }

  const ticks: Array<{ t: number; label: string }> = []
  {
    const span = to - from
    const step = span > 120 * DAY ? 30 * DAY : span > 40 * DAY ? 14 * DAY : 7 * DAY
    for (let t = from; t <= to; t += step) {
      const d = new Date(t)
      ticks.push({ t, label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) })
    }
  }

  return (
    <div className="chart">
      <div className="chart-bar">
        <div className="chips" style={{ marginBottom: 0 }}>
          <button className={`chip${window_ === 'cycle' ? ' is-active' : ''}`} onClick={() => setWindow('cycle')} disabled={!cycleStart}>This cycle</button>
          <button className={`chip${window_ === 'all' ? ' is-active' : ''}`} onClick={() => setWindow('all')}>Everything</button>
        </div>
        <span className="chart-note muted">Levels 1–5 · mean of observed skills per area · dashed = target by cycle end</span>
      </div>
      <svg className="chart-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Level over time by area">
        {[1, 2, 3, 4, 5].map((l) => (
          <g key={l}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(l)} y2={y(l)} className="chart-grid" />
            <text x={PAD.l - 8} y={y(l) + 3.5} className="chart-tick" textAnchor="end">{l}</text>
          </g>
        ))}
        {ticks.map((tk) => (
          <text key={tk.t} x={x(tk.t)} y={H - 6} className="chart-tick" textAnchor="middle">{tk.label}</text>
        ))}
        {now >= from && now <= to && (
          <line x1={x(now)} x2={x(now)} y1={PAD.t} y2={H - PAD.b} className="chart-now" />
        )}
        {lines.map((s) => s.d && (
          <path
            key={s.key}
            d={s.d}
            fill="none"
            stroke={domainColor(s.key)}
            strokeWidth={focus === s.key ? 3 : 1.75}
            opacity={focus && focus !== s.key ? 0.18 : 0.95}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {projections.map((p) => (
          <g key={p.key} opacity={focus && focus !== p.domain ? 0.15 : 0.85}>
            <path
              d={`M${x(now)},${y(p.from)} L${x(to)},${y(p.to)}`}
              fill="none" stroke={domainColor(p.domain)} strokeWidth={1.5} strokeDasharray="4 4"
            />
            <circle cx={x(to)} cy={y(p.to)} r={3} fill="none" stroke={domainColor(p.domain)} strokeWidth={1.5} />
          </g>
        ))}
      </svg>
      <ul className="chart-legend">
        {lines.map((s) => (
          <li key={s.key}>
            <button
              className={`chart-key${focus === s.key ? ' is-active' : ''}`}
              style={{ '--domain-color': domainColor(s.key) } as React.CSSProperties}
              onClick={() => setFocus(focus === s.key ? null : s.key)}
            >
              <span className="chart-key-swatch" aria-hidden="true" />
              <span className="chart-key-name">{domainLabel(s.key)}</span>
              {typeof s.last === 'number' && <span className="chart-key-val tnum">{s.last.toFixed(1)}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
